/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Component, ElementRef, Inject, OnDestroy, ViewChild } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { CARDS_API_DATA_SERVICE_PROVIDER } from '@app/app/page-modules/programs/services/create/core/cards-api-data.service';
import { CARDS_DATA_SERVICE, CardsDataService } from '@app/app/page-modules/programs/services/create/core/cards-data.service';
import { Select } from '@ngxs/store';
import { Observable } from 'rxjs';
import {
  Diagnostics,
  GeneratedReport,
  GeneratedReportConditionalSection,
  GeneratedReportSection,
  GeneratedReportStandardSection,
  Organization,
  ReportCompetencyLevelWrapper,
  ReportSection,
} from '../../../../../../shared/models';
import { MemberRecord } from '../../../../../../shared/models/admin/members.model';
import { ChartContent, ChartItem, ChartLearner } from '../../../../../../shared/models/editor/chart-content.model';
import { DEFAULT_LANGUAGE_CODE } from '../../../../../../shared/services/languages/language.service';
import { UserAuthState } from '../../../../../../user-auth/store/user-auth.state';
import { DIAGNOSTICS_DATA_SERVICE, DiagnosticsDataService } from '../../../../services/data.service';

/**
 * Body markup and styles mirror ptl-diagnostics-view-report on purpose: an admin downloading a
 * member's report must get the same PDF the member gets from the report page.
 */
@Component({
  selector: 'ptl-diagnostics-view-report-dialog',
  templateUrl: './report-dialog.component.html',
  styleUrls: ['./report-dialog.component.scss'],
  // The dialog also opens from the playlist and card member records, whose modules don't provide it.
  providers: [CARDS_API_DATA_SERVICE_PROVIDER],
})
export class DiagnosticsViewReportDialogComponent implements OnDestroy {
  @Select(UserAuthState.organizationDetails)
  organizationData$: Observable<Organization>;

  @ViewChild('reportPdf') reportPdf: ElementRef<HTMLElement>;

  completedData: Date;
  downloadInProgress = false;
  reportMode: string;
  report: GeneratedReport;
  reportSections: GeneratedReportSection[] = [];
  reportLoaded = false;
  isPercentageEnabled = false;
  member: MemberRecord;
  learner: ChartLearner;

  /**
   * The sections as the author saved them on the card. The generated report drops the competency
   * levels a segmented bar needs to draw its bands, so they are read off here, by position.
   */
  private authoredSections: ReportSection[] = [];
  private readonly noLevels: ReportCompetencyLevelWrapper[] = [];
  private currentLanguage = DEFAULT_LANGUAGE_CODE;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { assessmentUid: string; responsesUid: string; memberData: MemberRecord },
    @Inject(DIAGNOSTICS_DATA_SERVICE) private diagnosticsDataService: DiagnosticsDataService,
    @Inject(CARDS_DATA_SERVICE) private cardsDataService: CardsDataService,
    private dialogRef: MatDialogRef<DiagnosticsViewReportDialogComponent>,
  ) {
    if (data) {
      this.member = data.memberData;
      this.learner = { imageUrl: this.member?.thumbnail, firstName: this.member?.about?.firstName };
      this.loadAuthoredSections(data.assessmentUid);
      this.loadReport(data.assessmentUid, data.responsesUid);
    }
  }

  sectionTitle(section: GeneratedReportSection) {
    if (section.type === 'STANDARD_SECTION') {
      const tempElement = document.createElement('div');
      tempElement.innerText = '...';

      for (const sectionContent of (section as GeneratedReportStandardSection).dynamicContent) {
        if (typeof sectionContent.content === 'string') {
          tempElement.innerHTML = sectionContent.content;
          break;
        }
      }

      const content = tempElement.textContent || tempElement.innerText || '';

      if (content.length > 30) {
        return content.substring(0, 30) + '...';
      }

      return content;
    } else {
      return (section as GeneratedReportConditionalSection).learningGoalWrapper.tagTitle;
    }
  }

  isShownInIndex(section: GeneratedReportSection): boolean {
    if (section.type === 'STANDARD_SECTION') {
      return true;
    }
    return section.type === 'CONDITIONAL_SECTION' && this.report?.reportMode === 'FULL';
  }

  levelsForSection(index: number): ReportCompetencyLevelWrapper[] {
    const levels = this.authoredSections[index]?.learningGoalWrapper?.competencyLevels;
    if (!levels) {
      return this.noLevels;
    }
    return levels;
  }

  scrollTo(section: GeneratedReportSection) {
    const element = document.getElementById(section.uid);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  trackByFn(index: number, { uid }) {
    return uid || index;
  }

  back(): void {
    document.body.classList.remove('dialog-print');
    this.dialogRef.close();
  }

  downloadReport(): void {
    document.body.classList.add('dialog-print');
    window.print();
  }

  ngOnDestroy() {
    document.body.classList.remove('dialog-print');
  }

  private loadReport(assessmentUid: string, responsesUid: string): void {
    this.diagnosticsDataService.getReport(assessmentUid, responsesUid, null, this.currentLanguage).subscribe(({ isSuccess, value }) => {
      if (!isSuccess || !value) {
        this.reportLoaded = true;
        return;
      }
      this.report = value;
      this.reportMode = this.report.reportMode;
      this.mappingUidsInChartItems(this.report.sections);
      this.setSectionChartColors(this.report.sections);
      this.reportSections = this.report.sections;
      this.completedData = new Date();
      this.reportLoaded = true;
    });
  }

  private loadAuthoredSections(assessmentUid: string): void {
    this.cardsDataService.getCardById(assessmentUid).subscribe(({ isSuccess, value }) => {
      if (!isSuccess) {
        return;
      }
      const assessment = value as Diagnostics;
      this.authoredSections = assessment?.report?.sections ?? [];
    });
  }

  private mappingUidsInChartItems(sections: GeneratedReportSection[]) {
    sections.forEach((section: GeneratedReportSection) => {
      const castedSection = section as GeneratedReportStandardSection;
      if (!castedSection.dynamicContent) return;

      (section as GeneratedReportStandardSection).dynamicContent.forEach((contentItem) => {
        if (contentItem.type === 'CHART') {
          this.isPercentageEnabled = (contentItem as ChartContent).showPercentage;
          (contentItem as ChartContent).items.forEach((item: ChartItem) => {
            const itemUid = item.itemUid;

            sections.forEach((data: GeneratedReportSection) => {
              if (itemUid === (data as GeneratedReportConditionalSection)?.learningGoalWrapper?.tagUid) {
                item.sectionUid = data.uid;
              }
            });
          });
        }
      });
    });
  }

  private setSectionChartColors(sections: GeneratedReportSection[]) {
    const standardSections = sections.filter((item) => item.type === 'STANDARD_SECTION') as GeneratedReportStandardSection[];

    let chartContent = null;
    for (const section of standardSections) {
      for (const content of section.dynamicContent) {
        if (content.type === 'CHART') {
          chartContent = content;
          break;
        }
      }
      if (chartContent) {
        break;
      }
    }
    if (!chartContent) {
      return;
    }

    this.setConditionalSectionColors(chartContent, sections);
  }

  private setConditionalSectionColors(chartContent: ChartContent, sections: GeneratedReportSection[]) {
    const colors = {};
    chartContent.items.map((item) => {
      colors[item.itemUid] = item.color;
    });
    sections.map((section) => {
      if (section.type === 'CONDITIONAL_SECTION') {
        (section as GeneratedReportConditionalSection).chartColor =
          colors[(section as GeneratedReportConditionalSection).learningGoalWrapper.tagUid];
      }
    });
  }
}
